// Types
import {
  InfringementTableDataAction,
  InfringementTableDataActionType,
  InfringementTableDataState,
} from './types';

// Constants
import { OrderByDirectionEnum } from 'src/app/gql/__generated__/hooks';

export const infringementDataReducer = (
  state: InfringementTableDataState,
  action: InfringementTableDataAction
): InfringementTableDataState => {
  switch (action.type) {
    case InfringementTableDataActionType.UPDATE_FILTER: {
      // If filter value empty, we should remove the filter
      if (
        action.payload.value === null ||
        !action.payload.value.length ||
        action.payload.value === ''
      ) {
        const { [action.payload.filter]: _, ...newState } = state.variables.filter;
        return {
          ...state,
          variables: {
            ...state.variables,
            filter: newState,
          },
        };
      }
      return {
        ...state,
        variables: {
          ...state.variables,
          filter: {
            ...state.variables.filter,
            [action.payload.filter]: action.payload.value,
          },
        },
      };
    }
    case InfringementTableDataActionType.UPDATE_BOOLEAN_FILTER: {
      // If filter value is null, we should remove the filter
      if (action.payload.value === null) {
        const { [action.payload.filter]: _, ...newState } = state.variables.filter;
        return {
          ...state,
          variables: {
            ...state.variables,
            filter: newState,
          },
        };
      }
      return {
        ...state,
        variables: {
          ...state.variables,
          filter: {
            ...state.variables.filter,
            [action.payload.filter]: action.payload.value,
          },
        },
      };
    }
    case InfringementTableDataActionType.UPDATE_TAG_FILTER: {
      // If filter value is empty array or null, we should remove the filter
      if (
        !action.payload.value ||
        (Array.isArray(action.payload.value) && action.payload.value.length === 0)
      ) {
        const {
          [action.payload.resetFilter]: __,
          [action.payload.filter]: _,
          ...newState
        } = state.variables.filter;
        return {
          ...state,
          variables: {
            ...state.variables,
            filter: newState,
          },
        };
      }

      // Convert string/array of strings to array of objects with name property
      const tagValue = Array.isArray(action.payload.value)
        ? action.payload.value.map(tag => ({ name: tag }))
        : [{ name: action.payload.value }];

      // Remove resetFilter property if it exists
      const { [action.payload.resetFilter]: __, ...newFilter } = state.variables.filter;
      const updatedFilter = action.payload.resetFilter ? {} : newFilter;

      return {
        ...state,
        variables: {
          ...state.variables,
          filter: {
            ...updatedFilter,
            [action.payload.filter]: tagValue,
          },
        },
      };
    }
    case InfringementTableDataActionType.UPDATE_TIME_RANGE_FILTER: {
      const { start, end } = action.payload;
      // If start or end is not provided, we should remove the time range filter
      if (start === undefined || end === undefined) {
        const { timePeriod, ...newState } = state.variables.filter;
        return {
          ...state,
          variables: {
            ...state.variables,
            filter: newState,
          },
        };
      }
      return {
        ...state,
        variables: {
          ...state.variables,
          filter: {
            ...state.variables.filter,
            timePeriod: {
              start,
              end,
            },
          },
        },
      };
    }
    case InfringementTableDataActionType.CLEAR_FILTER: {
      const newFilter = { ...state.variables.filter };
      action.payload.filter.forEach(filterKey => {
        delete newFilter[filterKey];
      });
      return {
        ...state,
        variables: {
          ...state.variables,
          filter: newFilter,
        },
      };
    }
    case InfringementTableDataActionType.CLEAR_FILTERS: {
      return {
        ...state,
        hasCandidatesEnabled: false,
        variables: {
          ...state.variables,
          filter: {},
        },
      };
    }
    case InfringementTableDataActionType.TOGGLE_CANDIDATES: {
      return {
        ...state,
        hasCandidatesEnabled: action.payload,
      };
    }
    case InfringementTableDataActionType.SORT_TABLE: {
      const { sort } = state.variables;
      const sortArray = Array.isArray(sort) ? sort : sort ? [sort] : [];
      const currentSortField = sortArray.length > 0 ? sortArray[0].field : null;
      const currentSortDirection = sortArray.length > 0 ? sortArray[0].direction : null;

      // If sorting by the same field, toggle direction
      if (currentSortField === action.payload) {
        return {
          ...state,
          variables: {
            ...state.variables,
            sort: [
              {
                field: action.payload,
                direction:
                  currentSortDirection === OrderByDirectionEnum.Asc
                    ? OrderByDirectionEnum.Desc
                    : OrderByDirectionEnum.Asc,
              },
            ],
          },
        };
      }

      // If sorting by a new field, default to desc
      return {
        ...state,
        variables: {
          ...state.variables,
          sort: [
            {
              field: action.payload,
              direction: OrderByDirectionEnum.Desc,
            },
          ],
        },
      };
    }
    default:
      return state;
  }
};
